type TerminalOs = 'macos' | 'windows' | 'linux' | 'unknown';

export type TerminalErrorPresentation = {
  title: string;
  message: string;
  details: string;
};

function detectOs(): TerminalOs {
  if (typeof navigator === 'undefined') return 'unknown';
  const userAgent = navigator.userAgent || '';

  if (userAgent.includes('Macintosh')) return 'macos';
  if (userAgent.includes('Windows')) return 'windows';
  if (userAgent.includes('Linux')) return 'linux';
  return 'unknown';
}

export function getTerminalErrorPresentation(rawError: string): TerminalErrorPresentation {
  const details = rawError || 'Unknown terminal error';
  const os = detectOs();

  const isPosixSpawnFailure = details.includes('posix_spawnp failed');
  const isSpawnHelperOk = details.includes('spawn-helper (ok)');

  const isBuildReleaseSpawnHelperMissing = details.includes('build/Release/spawn-helper (missing)');
  const isPrebuildSpawnHelperOk =
    details.includes('node-pty/prebuilds/') && details.includes('spawn-helper (ok)');

  const isSpawnHelperMissing =
    details.includes('nodePtySpawnHelperCandidates=')
    && details.includes('spawn-helper (missing)')
    && !details.includes('spawn-helper (ok)')
    && !details.includes('spawn-helper (no-x)');

  const isTerminalSpawnFailure =
    details.includes('Failed to spawn terminal process')
    || details.includes('posix_spawnp failed')
    || details.includes('forkpty(3) failed');

  const isNodePtyMissing =
    details.includes('Failed to load native module: pty.node')
    || details.includes('nodePtyRoot=(unresolved)');

  const isMacOsTranslocated = /\bmacOSAppTranslocation=true\b/.test(details);

  // The native load error main recovers when node-pty's own message hides it,
  // e.g. "version `GLIBC_2.42' not found" (v1.5.1 Linux x64).
  const missingGlibc = /version `(GLIBC_[\d.]+)' not found/.exec(details)?.[1];

  if (missingGlibc) {
    return {
      title: 'Terminal needs a newer Linux',
      message: `The terminal's native component in this build requires ${missingGlibc.replace('_', ' ')}, which is newer than this system's C library. The rest of the app is unaffected — update Jam SQL Studio and try again.`,
      details,
    };
  }

  if (isNodePtyMissing) {
    return {
      title: 'Terminal backend unavailable',
      message:
        'This build is missing the native PTY backend (node-pty). Reinstall or rebuild the packaged app, then try creating the terminal again.',
      details,
    };
  }

  if (isSpawnHelperMissing) {
    return {
      title: 'Terminal helper missing from this build',
      message:
        "This packaged app is missing node-pty's `spawn-helper` binary, so the integrated terminal can't start. Install an updated build and try again (developers: ensure the build unpacks `node_modules/node-pty/prebuilds/**`).",
      details,
    };
  }

  if (isTerminalSpawnFailure) {
    if (os === 'macos') {
      if (isPosixSpawnFailure && isBuildReleaseSpawnHelperMissing && isPrebuildSpawnHelperOk) {
        return {
          title: 'Terminal helper not unpacked in this build',
          message:
            "This build includes node-pty's native addon from `build/Release`, but its `spawn-helper` binary isn't unpacked outside the ASAR, so macOS can't execute it. Reinstall/update the app; developers: ensure ASAR unpack includes `**/node_modules/node-pty/build/**/spawn-helper` (Electron Forge: `forge.config.js` → `packagerConfig.asar.unpack`).",
          details,
        };
      }

      if (isPosixSpawnFailure && isSpawnHelperOk && !isMacOsTranslocated) {
        return {
          title: 'Terminal blocked by macOS security',
          message:
            "macOS refused to execute the terminal helper process. This is commonly caused by Gatekeeper/quarantine (especially for unsigned/dev builds). Reinstall from a signed build if available; for local builds you may need to remove quarantine (e.g. `xattr -dr com.apple.quarantine \"/Applications/sql-studio.app\"`) and reopen the app.",
          details,
        };
      }

      if (isMacOsTranslocated) {
        return {
          title: 'Terminal blocked by macOS security',
          message:
            'SQL Studio is running under App Translocation (quarantine), which can block spawning the terminal helper process. Quit the app, move it to /Applications (drag from the .dmg), reopen it, then try again.',
          details,
        };
      }

      return {
        title: 'Terminal failed to start',
        message:
          "macOS blocked SQL Studio from starting the terminal helper process. Check Gatekeeper/quarantine settings and any security tools, then try creating the terminal again.",
        details,
      };
    }

    if (os === 'windows') {
      return {
        title: 'Terminal failed to start',
        message:
          'Windows blocked SQL Studio from starting the terminal process. Check Windows Defender Firewall / antivirus rules, then try creating the terminal again.',
        details,
      };
    }

    if (os === 'linux') {
      return {
        title: 'Terminal failed to start',
        message:
          'The OS blocked SQL Studio from starting the terminal process. Check firewall/sandbox settings (Flatpak/Snap) and executable permissions, then try again.',
        details,
      };
    }

    return {
      title: 'Terminal failed to start',
      message: 'SQL Studio could not start the terminal process. Fix any OS restrictions and try again.',
      details,
    };
  }

  return {
    title: 'Terminal error',
    message: 'Terminal could not be started. Fix the underlying issue and try again.',
    details,
  };
}
